import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMap } from '../../context/MapContext';
import './MunicipalitySelector.css';

const BAR_PERIODS = [
  { id: 'situacional', label: 'SITUACIONAL', years: '2024–2026', phase: 'Elaboração do PMSB', color: '#0d7c8a', colorDark: '#0a6370', colorBg: '#e0f5f7' },
  { id: 'imediata',    label: 'IMEDIATA',    years: '2027–2029', phase: 'Implementação',       color: '#c8960a', colorDark: '#a87c08', colorBg: '#fef9e0' },
  { id: 'curto',       label: 'CURTO PRAZO', years: '2030–2034', phase: 'Implementação',       color: '#c86020', colorDark: '#a84d18', colorBg: '#fef0e0' },
  { id: 'medio',       label: 'MÉDIO PRAZO', years: '2035–2038', phase: 'Implementação',       color: '#8b3a30', colorDark: '#722f26', colorBg: '#fde8e6' },
  { id: 'longo',       label: 'LONGO PRAZO', years: '2039–2046', phase: 'Implementação',       color: '#5c6e25', colorDark: '#4a591e', colorBg: '#ecf1e0' },
];

export const MUNICIPALITIES = [
  { name: 'Canudos',            state: 'BA', code: '2906824' },
  { name: 'Cardeal da Silva',   state: 'BA', code: '2907004' },
  { name: 'Triunfo',            state: 'PE', code: '2615706' },
  { name: 'Cabrobó',            state: 'PE', code: '2603009' },
  { name: 'Coronel João de Sá', state: 'BA', code: '2909208' },
];

/* Bandeiras dos estados (Wikimedia Commons — domínio público) */
const STATE_FLAGS = {
  BA: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/Bandeira_da_Bahia.svg/40px-Bandeira_da_Bahia.svg.png',
  PE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/59/Bandeira_de_Pernambuco.svg/40px-Bandeira_de_Pernambuco.svg.png',
  MG: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f4/Bandeira_de_Minas_Gerais.svg/40px-Bandeira_de_Minas_Gerais.svg.png',
  SP: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Bandeira_do_estado_de_S%C3%A3o_Paulo.svg/40px-Bandeira_do_estado_de_S%C3%A3o_Paulo.svg.png',
  RJ: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Bandeira_do_estado_do_Rio_de_Janeiro.svg/40px-Bandeira_do_estado_do_Rio_de_Janeiro.svg.png',
  CE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2e/Bandeira_do_Cear%C3%A1.svg/40px-Bandeira_do_Cear%C3%A1.svg.png',
  PI: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Bandeira_do_Piau%C3%AD.svg/40px-Bandeira_do_Piau%C3%AD.svg.png',
  MA: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/45/Bandeira_do_Maranh%C3%A3o.svg/40px-Bandeira_do_Maranh%C3%A3o.svg.png',
  PB: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Bandeira_da_Para%C3%ADba.svg/40px-Bandeira_da_Para%C3%ADba.svg.png',
  RN: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Bandeira_do_Rio_Grande_do_Norte.svg/40px-Bandeira_do_Rio_Grande_do_Norte.svg.png',
  AL: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/Bandeira_de_Alagoas.svg/40px-Bandeira_de_Alagoas.svg.png',
  SE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/Bandeira_de_Sergipe.svg/40px-Bandeira_de_Sergipe.svg.png',
};

function normalize(s) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function StateFlag({ state, size = 20 }) {
  const url = STATE_FLAGS[state];
  if (!url) return <span className="ms-badge ms-badge-fallback">{state}</span>;
  return (
    <img
      src={url}
      alt={`Bandeira ${state}`}
      className="ms-flag"
      style={{ height: size }}
      loading="lazy"
    />
  );
}

export default function MunicipalitySelector() {
  const { selectedMunicipality, setSelectedMunicipality, selectedPeriod, setSelectedPeriod } = useMap();
  const [open,       setOpen]       = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [search,     setSearch]     = useState('');
  const wrapRef       = useRef(null);
  const periodWrapRef = useRef(null);
  const inputRef      = useRef(null);

  /* Fecha ao clicar fora */
  useEffect(() => {
    function onPointer(e) {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
      if (periodOpen && periodWrapRef.current && !periodWrapRef.current.contains(e.target)) {
        setPeriodOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, periodOpen]);

  /* Foca o input ao abrir */
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const filtered = MUNICIPALITIES.filter((m) => {
    const q = normalize(search);
    return !q || normalize(m.name).includes(q) || normalize(m.state).includes(q);
  });

  const handleSelect = useCallback((m) => {
    setSelectedMunicipality(m);
    setOpen(false);
    setSearch('');
  }, [setSelectedMunicipality]);

  function handleToggle() {
    setOpen((v) => !v);
    if (!open) setSearch('');
  }

  return (
    <div className="ms-bar-container">
    {/* Seletor de período — lado esquerdo */}
    <div className="ms-period-wrap" ref={periodWrapRef}>
      <button
        className={`ms-period-trigger${periodOpen ? ' ms-period-trigger--open' : ''}`}
        onClick={() => setPeriodOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={periodOpen}
        aria-label="Selecionar período do PMSB"
        title="Selecionar período do PMSB"
      >
        <span className="ms-bar-label">Período</span>
        {selectedPeriod && (
          <span className="ms-period-dot" style={{ background: selectedPeriod.color }} aria-hidden="true" />
        )}
        <span className="ms-period-trigger-label">
          {selectedPeriod ? selectedPeriod.label : 'Selecione…'}
        </span>
        <svg
          className={`ms-trigger-chevron${periodOpen ? ' ms-trigger-chevron--open' : ''}`}
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {periodOpen && (
        <div className="ms-period-dropdown" role="listbox" aria-label="Horizontes do PMSB">
          {BAR_PERIODS.map((p) => (
            <button
              key={p.id}
              className={`ms-period-item${selectedPeriod?.id === p.id ? ' ms-period-item--active' : ''}`}
              onClick={() => { setSelectedPeriod(p); setPeriodOpen(false); }}
              role="option"
              aria-selected={selectedPeriod?.id === p.id}
              title={p.label}
            >
              <span className="ms-period-dot" style={{ background: p.color }} aria-hidden="true" />
              <span className="ms-period-item-label">{p.label}</span>
              <span className="ms-period-item-years">{p.years}</span>
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Seletor de município — lado direito */}
    <div className="ms-wrap" ref={wrapRef}>
      {/* Botão trigger — barra full-width */}
      <button
        className={`ms-trigger${!selectedMunicipality ? ' ms-trigger--empty' : ''}${open ? ' ms-trigger--open' : ''}`}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Selecionar município"
        title="Selecionar município"
      >
        {/* Rótulo fixo */}
        <span className="ms-bar-label">Município</span>

        {/* Ícone de pin */}
        <svg className="ms-trigger-pin" width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>

        {selectedMunicipality ? (
          <span className="ms-trigger-label">
            <StateFlag state={selectedMunicipality.state} size={18} />
            <span className="ms-trigger-state">{selectedMunicipality.state}</span>
            <span className="ms-trigger-name">{selectedMunicipality.name}</span>
          </span>
        ) : (
          <span className="ms-trigger-placeholder">Selecione o município…</span>
        )}

        {selectedMunicipality && (
          <span className="ms-trigger-change">Alterar</span>
        )}

        <svg className={`ms-trigger-chevron${open ? ' ms-trigger-chevron--open' : ''}`}
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="ms-dropdown" role="listbox" aria-label="Lista de municípios">
          {/* Busca */}
          <div className="ms-search-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round"
              className="ms-search-icon" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={inputRef}
              className="ms-search-input"
              type="search"
              placeholder="Nome ou estado…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buscar município"
            />
          </div>

          {/* Lista */}
          <ul className="ms-list">
            {filtered.length === 0 && (
              <li className="ms-list-empty">Nenhum município encontrado.</li>
            )}
            {filtered.map((m) => (
              <li key={m.code}>
                <button
                  className={`ms-list-item${selectedMunicipality?.code === m.code ? ' ms-list-item--active' : ''}`}
                  onClick={() => handleSelect(m)}
                  role="option"
                  aria-selected={selectedMunicipality?.code === m.code}
                >
                  <StateFlag state={m.state} size={18} />
                  <span className="ms-list-state">{m.state}</span>
                  <span className="ms-list-name">{m.name}</span>
                  {selectedMunicipality?.code === m.code && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="#2799C5" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>

    </div>

  );
}
